import { GlobalStore } from '@roc/feature-app-core';
import { action, computed, makeObservable, observable } from 'mobx';
import { OneToolService } from '../../services/oneToolService';
import { SelectEntityOption } from '../../utils/constants';
import { QuoteTypeStore } from '../quoteTypeStore';
import { BorrowerFormStore } from './borrowerFormStore';
import { LoanSubType } from '@roc/feature-utils';

const YES_OPTIONS_BANKRUPTCY_FORECLOSURE = ["Y", "Yes( >4 years old)", "Yes( <4 years old)"];

export class BorrowersStore {
  private globalStore: GlobalStore;
  private quoteTypeStore: QuoteTypeStore;
  private oneToolService: OneToolService;

  public borrowerFormStores: BorrowerFormStore[];

  constructor(globalStore, quoteTypeStore) {
    this.globalStore = globalStore;
    this.quoteTypeStore = quoteTypeStore;
    this.oneToolService = new OneToolService();
    makeObservable(this, {
      reset: action,
      borrowerFormStores: observable,
      createBorrowerForm: action,
      addNewBorrower: action,
      addBorrower: action,
      removeBorrower: action,
      validateAllBorrowers: action,
      turnOffBorrowersEditMode: action,
      getBorrowers: action,
      loadBorrowers: action,
      totalOwnership: computed,
      showTotalOwnershipWarning: computed,
      resetBorrowerFormStores: action,
      showOwnershipPercentage: computed,
      setFieldError: action
    });
  }

  reset() {
    this.resetBorrowerFormStores();
    this.addNewBorrower();
  }

  resetBorrowerFormStores() {
    this.borrowerFormStores = [];
  }

  createBorrowerForm() {
    return new BorrowerFormStore(this.globalStore, this.quoteTypeStore.loanSubtype);
  }

  addNewBorrower() {
    this.borrowerFormStores.push(this.createBorrowerForm());
  }

  addBorrower(borrowerFormStore: BorrowerFormStore) {
    this.borrowerFormStores.push(borrowerFormStore);
  }

  removeBorrower(borrowerKey: string) {
    this.borrowerFormStores = this.borrowerFormStores.filter(
      borrower => borrower.key !== borrowerKey
    );
  }
  checkIfPersonalGuarantor(item: BorrowerFormStore) {
    if (item.form.fields.personalGuarantor) {
      return item.form.fields.percentageOfOwnership.value;
    }
    else return 0;
  }
  get totalOwnership() {
    return this.borrowerFormStores.reduce((total, item) => total + this.checkIfPersonalGuarantor(item), 0);
  }

  get showTotalOwnershipWarning() {
    return this.totalOwnership !== 100 && this.borrowerFormStores.length > 1;
  }

  setFieldError(store, fieldName, error) {
    store.form.fields[fieldName].error = error;
  }

  validateAllBorrowers() {
    let isValid = true;
    let thereIsAtLeastOnePG = false;
    this.borrowerFormStores.forEach(store => {
      thereIsAtLeastOnePG =
        thereIsAtLeastOnePG || store.form.fields.personalGuarantor.value;
      store.validateBorrower();
      if ([LoanSubType.SINGLE_PROPERTY, LoanSubType.RENTAL_PORTFOLIO].includes(this.quoteTypeStore.loanSubtype)) {
        const bankruptcy = store.form.fields.bankruptcy.value;
        const threeYearsAgo = new Date();
        threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);

        if (bankruptcy && YES_OPTIONS_BANKRUPTCY_FORECLOSURE.includes(bankruptcy)) {
          const bankruptcyDate = this.convertToDate(store.form.fields.bankruptcyDate.value);
          if (!bankruptcyDate) {
            this.setFieldError(store, "bankruptcyDate", "This field is required");
            isValid = false;
          } else {
            if (bankruptcyDate > threeYearsAgo) {
              this.setFieldError(store, "bankruptcyDate", "Bankruptcy Date must be no less than 3 Years");
              isValid = false;
            }
          }
        }

        const foreclosure = store.form.fields.foreclosure.value;
        if (foreclosure && YES_OPTIONS_BANKRUPTCY_FORECLOSURE.includes(foreclosure)) {
          const foreclosureDate = this.convertToDate(store.form.fields.foreclosureDate.value);
          if (!foreclosureDate) {
            this.setFieldError(store, "foreclosureDate", "This field is required");
            isValid = false;
          } else {
            if (foreclosureDate && foreclosureDate > threeYearsAgo) {
              this.setFieldError(store, "foreclosureDate", "Foreclosure Date must be no less than 3 Years");
              isValid = false;
            }
          }
        }
      }
      isValid = isValid && store.form.meta.isValid;
      if (this.borrowerFormStores.length === 1) {
        store.onFieldChange('percentageOfOwnership', 100);
      }
      store.setEditMode(!isValid);
      store.setShowErrors(!store.form.meta.isValid);
    });

    if (!isValid) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please complete all the required fields for the borrowers.',
      });
    }

    if (!thereIsAtLeastOnePG) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'At least one Borrower should be Personal Guarantor.',
      });
      isValid = false;
    }

    return isValid;
  }

  turnOffBorrowersEditMode() {
    this.borrowerFormStores.forEach(borrowerFormStore => {
      borrowerFormStore.form.meta.editMode = false;
    });
  }

  getBorrowers() {
    return this.borrowerFormStores.map(form => form.getBorrower());
  }

  loadBorrowers(borrowers) {
    if (borrowers?.length > 0) {
      this.borrowerFormStores = borrowers?.map(borrower => {
        const form = this.createBorrowerForm();
        form.loadBorrower(borrower);
        form.setEditMode(false);
        return form;
      });
    }
  }

  get showOwnershipPercentage() {
    const { entityFormStore } = this.quoteTypeStore.entityStore;
    const hasEntity = entityFormStore.form.fields.hasEntity.value;
    return hasEntity && this.borrowerFormStores.length > 1;
  }

  convertToDate(strDate) {
    try {
      if (!strDate || strDate === "") {
        return null;
      }
      const [month, day, year] = strDate.split("/").map(Number);
      const date = new Date(year, month - 1, day);
      return date;
    } catch (error) {
      console.log(error);
      return null;
    }
  }
}
